import { useState, useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import type { DeliveryFormValues } from "../lib/schemas/travel-booking";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash } from "lucide-react";
import { DeliveryModal } from "./delivery-modal";
import { useFieldArray } from "react-hook-form";
import { RxExclamationTriangle } from "react-icons/rx";

interface DeliveryStepProps {
  form: UseFormReturn<any>;
}

export function DeliveryStep({ form }: DeliveryStepProps) {
  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "deliveries",
  });

  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [currentDeliveryIndex, setCurrentDeliveryIndex] = useState<
    number | null
  >(null);
  const [editMode, setEditMode] = useState(false);

  // Show toast when KYC is verified (component mounts)
  useEffect(() => {
    const customer = form.getValues('customers.0');
    const kycStatus = (customer as any)?.kyc_status;
    
    if (kycStatus && (String(kycStatus).toLowerCase() === 'active' || String(kycStatus).toLowerCase() === 'verified')) {
      // toast.success("KYC is verified.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  const handleAddDelivery = (delivery: DeliveryFormValues) => {
    if (editMode && currentDeliveryIndex !== null) {
      update(currentDeliveryIndex, delivery);
    } else {
      append(delivery);
    }
    setIsDeliveryModalOpen(false);
    setEditMode(false);
    setCurrentDeliveryIndex(null);
  };

  const handleEditDelivery = (index: number) => {
    setCurrentDeliveryIndex(index);
    setEditMode(true);
    setIsDeliveryModalOpen(true);
  };

  // Create options for the delivery modal from customers and their products
  const customers = form.getValues("customers") || [];
  // const customerOptions = customers.map((customer: any, index: number) => ({
  //   value: `customer-${index}`,
  //   label: customer?.fullName || `Customer ${index + 1}`,
  // }))

  console.log("Customers in Delivery Step:", customers);

  const productOptions = customers.flatMap(
    (customer: any, customerIndex: number) =>
      (customer?.products || []).map((product: any, productIndex: number) => ({
        value: product?.local_id,
        label: `${product?.productType?.label} - ${product?.currencies?.[0].currency?.label} ${product?.currencies?.[0]?.amount}`,
        customerIndex,
        productIndex,
      }))
  );
  console.log("Customers in Delivery Step:", customers);
  console.log("Product Options in Delivery Step:", productOptions);

  const incompleteDeliveries = form.getValues("deliveries").filter((delivery: any) => {
    return delivery?.products?.length === 0 || delivery?.customers?.length === 0;
  });

  return (
    <div className="space-y-6">
      <h3 className="text-lg flex items-center font-semibold">
        Delivery
        {
          fields.length === 0 &&
          <Button
            onClick={() => {
              setIsDeliveryModalOpen(true);
              setEditMode(false);
              setCurrentDeliveryIndex(null);
            }}
            className="text-blue-700"
          >
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 15.7109C4.14 15.7109 1 12.5709 1 8.71094C1 4.85094 4.14 1.71094 8 1.71094C11.86 1.71094 15 4.85094 15 8.71094C15 12.5709 11.86 15.7109 8 15.7109ZM8 2.71094C4.69 2.71094 2 5.40094 2 8.71094C2 12.0209 4.69 14.7109 8 14.7109C11.31 14.7109 14 12.0209 14 8.71094C14 5.40094 11.31 2.71094 8 2.71094Z"
                fill="#165FE3"
              />
              <path
                d="M8 12.2109C7.72 12.2109 7.5 11.9909 7.5 11.7109V5.71094C7.5 5.43094 7.72 5.21094 8 5.21094C8.28 5.21094 8.5 5.43094 8.5 5.71094V11.7109C8.5 11.9909 8.28 12.2109 8 12.2109Z"
                fill="#165FE3"
              />
              <path
                d="M11 9.21094H5C4.72 9.21094 4.5 8.99094 4.5 8.71094C4.5 8.43094 4.72 8.21094 5 8.21094H11C11.28 8.21094 11.5 8.43094 11.5 8.71094C11.5 8.99094 11.28 9.21094 11 9.21094Z"
                fill="#165FE3"
              />
            </svg>
            Add
          </Button>
        }
      </h3>
      {fields.length > 0 ? (
        <div className="space-y-4">
          {
            incompleteDeliveries.length > 0 && (
              <h4 className="text-sm flex items-center font-medium text-red-700 gap-2"> <RxExclamationTriangle/> Action required</h4>
            )
          }
          <div className={`overflow-hidden ${incompleteDeliveries.length > 0 ? "border border-red-500 rounded-xl" : "shadow-sm"}`}>
            <Table className="min-w-max bg-transparent">
              <TableHeader className="bg-[#EEEEEE] ">
                <TableRow className="text-[#666666] font-semibold text-base leading-5">
                  <TableHead className="px-6 py-3 text-left">
                    Delivery Mode
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left">
                    Delivery ID
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left">Type</TableHead>
                  <TableHead className="px-6 py-3 text-left">Customers</TableHead>
                  <TableHead className="px-6 py-3 text-left">Products</TableHead>
                  <TableHead className="px-6 py-3 text-left">Addresses</TableHead>
                  <TableHead className="px-6 py-3 text-left"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white-A700">
                {fields.map((field, index) => {
                  const delivery = form.getValues(
                    `deliveries.${index}`
                  ) as DeliveryFormValues;

                  const address = delivery?.addresses?.[0];
                  const joinedAddress = [
                    address?.address1,
                    address?.address2,
                    address?.city,
                    address?.landmark,
                    address?.state?.label,
                    address?.country?.label,
                    address?.pincode,
                  ].filter(Boolean).join(", ") || "-";
                  return (
                    <TableRow
                      key={field.id}
                      className="px-6 border-0"
                    >
                      <TableCell className="px-6 py-4 text-[#222] font-medium text-base">
                        {delivery?.deliveryMode?.label || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                        {delivery?.deliveryId || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                        {delivery?.deliveryType || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                        {delivery?.customers.label}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                        <div className="flex items-start flex-col gap-0.5">
                          {
                            delivery?.products?.map(product => <p>{product.label}</p>)|| "—"
                          }
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#222] font-normal text-base max-w-[200px]">
                        {joinedAddress}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <div className="flex flex-col space-x-2">
                          <div className="flex flex-row gap-3">
                            <button
                              className="text-[#176bef] hover:underline flex items-center"
                              onClick={() => handleEditDelivery(index)}
                              title="Edit"
                              type="button"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                            </button>
                            <button
                              className="text-[#ff4d4f] hover:underline flex items-center"
                              onClick={() => remove(index)}
                              title="Delete"
                              type="button"
                            >
                              <Trash className="h-4 w-4 mr-1" />
                            </button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="text-center bg-gray-100 py-36 border rounded-md">
          <p className="text-[#555555] flex items-center gap-2 justify-center text-gray-400">
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 15.7109C4.14 15.7109 1 12.5709 1 8.71094C1 4.85094 4.14 1.71094 8 1.71094C11.86 1.71094 15 4.85094 15 8.71094C15 12.5709 11.86 15.7109 8 15.7109ZM8 2.71094C4.69 2.71094 2 5.40094 2 8.71094C2 12.0209 4.69 14.7109 8 14.7109C11.31 14.7109 14 12.0209 14 8.71094C14 5.40094 11.31 2.71094 8 2.71094Z"
                fill="#165FE3"
              />
              <path
                d="M8 12.2109C7.72 12.2109 7.5 11.9909 7.5 11.7109V5.71094C7.5 5.43094 7.72 5.21094 8 5.21094C8.28 5.21094 8.5 5.43094 8.5 5.71094V11.7109C8.5 11.9909 8.28 12.2109 8 12.2109Z"
                fill="#165FE3"
              />
              <path
                d="M11 9.21094H5C4.72 9.21094 4.5 8.99094 4.5 8.71094C4.5 8.43094 4.72 8.21094 5 8.21094H11C11.28 8.21094 11.5 8.43094 11.5 8.71094C11.5 8.99094 11.28 9.21094 11 9.21094Z"
                fill="#165FE3"
              />
            </svg>
            Add Delivery to view here
          </p>
        </div>
      )}

      {isDeliveryModalOpen && (
        <DeliveryModal
          isOpen={isDeliveryModalOpen}
          onClose={() => setIsDeliveryModalOpen(false)}
          onSubmit={handleAddDelivery}
          initialData={
            editMode && currentDeliveryIndex !== null
              ? {
                  ...form.getValues(`deliveries.${currentDeliveryIndex}`),
                  deliveryType:'self',
                  deliveryMode:{
                    label: 'Self',
                    value: 'self',
                  },
                  products: productOptions,                
                }
              : undefined
          }
          customers={customers}
          productOptions={productOptions}
          showStatusSection={false}
        />
      )}
    </div>
  );
}
